// ---------------------------------------------------------------------------
// Scroll parallax for marginal words below the fold.
//
// A section opts in with `data-parallax-word` — optionally carrying the travel
// in pixels, e.g. `data-parallax-word="300"` — and the marginal word inside it
// drifts downward as the section crosses the viewport, the same gesture the
// homepage hero makes.
//
// The hero is not driven from here. It sits at the top of the page, so its
// photo and word run on a CSS scroll timeline tied to the page's own scroll
// (HERO PARALLAX in main.css), and its word performs a measured two-word
// reveal rather than a fixed drift. Anything further down the page has no such
// fixed relationship to the scroll position and has to work from its own
// progress across the viewport, which is what this does.
//
// Where scroll-driven animations are supported this runs once and only passes
// each section's travel to CSS (MARGINAL WORD DRIFT in main.css), which does
// the moving; the per-frame code below is the fallback for everything else.
//
// Purely decorative, so it does nothing at all under reduced motion — the word
// simply sits where the CSS put it.
// ---------------------------------------------------------------------------

// Chosen to land near the hero's rate. The word travels this far across a full
// pass of the section, so against a typical viewport plus a band a few hundred
// pixels tall that works out around 0.25× the scroll — the hero's is 0.4×, over
// a much taller element.
const DEFAULT_TRAVEL = 320;

let items = [];
let ticking = false;

function update() {
  const viewport = window.innerHeight;

  items.forEach(({ container, word, travel }) => {
    const rect = container.getBoundingClientRect();

    // Nothing to do for a section that isn't on screen — and skip the word
    // while a breakpoint has it hidden, where it has no box to move.
    if (rect.bottom < 0 || rect.top > viewport) return;
    if (!word.getClientRects().length) return;

    // 0 as the section's top reaches the bottom of the viewport, 1 as its
    // bottom leaves the top: one full pass, however tall the section is.
    const progress = (viewport - rect.top) / (viewport + rect.height);
    const offset = (Math.min(Math.max(progress, 0), 1) - 0.5) * travel;

    // The individual `translate` property, so it composes with the rotate the
    // marginal word is already carrying instead of overwriting it.
    word.style.translate = `0 ${offset.toFixed(1)}px`;
  });
}

function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => { update(); ticking = false; });
}

export default function initParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const containers = [...document.querySelectorAll('[data-parallax-word]')];
  if (!containers.length) return;

  // Where the browser can run scroll-driven animations, CSS moves the word
  // (MARGINAL WORD DRIFT in main.css) and all this does is hand it each
  // section's travel. No scroll listener at all.
  if (window.CSS && CSS.supports('animation-timeline: view()')) {
    containers.forEach(container => {
      const travel = Number(container.dataset.parallaxWord) || DEFAULT_TRAVEL;
      container.style.setProperty('--parallax-travel', `${travel}px`);
    });
    return;
  }

  items = containers
    .map(container => {
      const word = container.querySelector('[data-component=marginal-word]');
      if (!word) return null;

      const travel = Number(container.dataset.parallaxWord) || DEFAULT_TRAVEL;
      word.style.willChange = 'translate';

      return { container, word, travel };
    })
    .filter(Boolean);

  if (!items.length) return;

  update();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
}
